import { Box } from '@mui/material'

import { formatCurrency } from '../../../utils'
import ChartCard from '../cases/caseCharts/ChartCard'
import SummaryBarChart from '../cases/caseCharts/SummaryBarChart'
import { CHART_HEIGHT, formatAxisCurrency } from '../cases/caseCharts/constants'
import { CARD_SUBTITLE_SX, DASHBOARD_BAR_COLOR, formatCount } from './constants'

const ROW_HEIGHT = 32
const AXIS_ALLOWANCE = 40
/** ChartCard's plot area padding (p={2}), top + bottom. */
const CARD_PADDING = 32
/** Past this the horizontal chart scrolls inside its card instead of growing the page. */
const MAX_HORIZONTAL_HEIGHT = 480

export interface DashboardBar {
  label: string
  value: number
}

interface DashboardBarChartProps {
  title: string
  subtitle?: string
  tooltip?: string
  data?: DashboardBar[]
  /** How bar values, labels and the value axis are printed. */
  valueType?: 'count' | 'currency'
  loading?: boolean
  /** 'horizontal' for long, open-ended category lists (law types, attorneys). */
  orientation?: 'vertical' | 'horizontal'
  categoryWidth?: number
}

const VALUE_FORMATS = {
  count: { formatValue: formatCount, formatAxis: formatCount },
  currency: { formatValue: formatCurrency, formatAxis: formatAxisCurrency }
}

const DashboardBarChart = ({
  title,
  subtitle,
  tooltip,
  data = [],
  valueType = 'count',
  loading,
  orientation = 'vertical',
  categoryWidth
}: DashboardBarChartProps) => {
  const isHorizontal = orientation === 'horizontal'
  // Horizontal bars keep a fixed row height, so the plot grows with the list and scrolls in the card
  const plotHeight = isHorizontal ? data.length * ROW_HEIGHT + AXIS_ALLOWANCE : '100%'
  const cardHeight = isHorizontal
    ? Math.min(Math.max(data.length * ROW_HEIGHT + AXIS_ALLOWANCE + CARD_PADDING, 200), MAX_HORIZONTAL_HEIGHT)
    : CHART_HEIGHT

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      subtitleSx={CARD_SUBTITLE_SX}
      tooltip={tooltip}
      loading={loading}
      isEmpty={!data.some(datum => datum.value !== 0)}
      emptyMessage="No data for the selected period."
      height={cardHeight}
    >
      <Box height="100%" overflow="auto">
        <Box height={plotHeight} minWidth={isHorizontal ? 360 : undefined}>
          <SummaryBarChart
            data={data.map(datum => ({ name: datum.label, value: datum.value, color: DASHBOARD_BAR_COLOR }))}
            orientation={orientation}
            {...VALUE_FORMATS[valueType]}
            categoryWidth={categoryWidth}
            animate={false}
          />
        </Box>
      </Box>
    </ChartCard>
  )
}

export default DashboardBarChart
